import { useCallback, useEffect, useMemo, useState } from 'react';

import api from 'api';
import {
    INITIAL_DOCS,
    emptyDocsState,
    emptyFlagState,
    hasResource,
} from 'helpers/initialDocuments';
import type { InitialDocConfig, InitialDocFlagState, InitialDocsState } from 'types';
import {
    INITIAL_DOCUMENTS_REFRESH_EVENT,
    type InitialDocumentsRefreshDetail,
} from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/CLIENTVIEW/components/ClientProfile/components/InitialDocuments/initialDocumentsEvents';

interface UseInitialDocumentsResult {
    docs: InitialDocsState;
    loading: boolean;
    error: boolean;
    uploading: InitialDocFlagState;
    viewing: InitialDocFlagState;
    providedCount: number;
    fetchAll: () => Promise<void>;
    upload: (cfg: InitialDocConfig, file: File) => Promise<boolean>;
    getPreview: (
        cfg: InitialDocConfig
    ) => Promise<{ url: string; fileName: string; titleKey: string } | null>;
}

const useInitialDocuments = (userId: number | string | null): UseInitialDocumentsResult => {
    const [docs, setDocs] = useState<InitialDocsState>(emptyDocsState);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [uploading, setUploading] = useState<InitialDocFlagState>(emptyFlagState);
    const [viewing, setViewing] = useState<InitialDocFlagState>(emptyFlagState);

    const fetchAll = useCallback(async () => {
        if (!userId) {
            setDocs(emptyDocsState());
            setError(false);
            return;
        }

        setLoading(true);
        setError(false);
        try {
            const results = await Promise.all(
                INITIAL_DOCS.map((cfg) => api.userDocuments.get(userId, cfg.nameEn, 'USER'))
            );
            const next = emptyDocsState();
            INITIAL_DOCS.forEach((cfg, idx) => {
                const data = results[idx]?.data;
                next[cfg.key] = data && Object.keys(data).length > 0 ? data : null;
            });
            setDocs(next);
        } catch {
            setError(true);
            setDocs(emptyDocsState());
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        void fetchAll();
    }, [fetchAll]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handler = (event: Event) => {
            const detail = (event as CustomEvent<InitialDocumentsRefreshDetail>).detail;
            if (
                detail?.userId != null &&
                userId != null &&
                String(detail.userId) !== String(userId)
            ) {
                return;
            }
            void fetchAll();
        };

        window.addEventListener(INITIAL_DOCUMENTS_REFRESH_EVENT, handler);
        return () => window.removeEventListener(INITIAL_DOCUMENTS_REFRESH_EVENT, handler);
    }, [fetchAll, userId]);

    const upload = useCallback(
        async (cfg: InitialDocConfig, file: File): Promise<boolean> => {
            if (!userId) return false;

            setUploading((prev) => ({ ...prev, [cfg.key]: true }));
            try {
                const resp = await api.userDocuments.post(userId, cfg.nameEn, file);
                setDocs((prev) => ({ ...prev, [cfg.key]: resp.data ?? null }));
                return true;
            } catch {
                return false;
            } finally {
                setUploading((prev) => ({ ...prev, [cfg.key]: false }));
            }
        },
        [userId]
    );

    const getPreview = useCallback(
        async (cfg: InitialDocConfig) => {
            const doc = docs[cfg.key];
            if (!doc?.id || !userId) return null;

            setViewing((prev) => ({ ...prev, [cfg.key]: true }));
            try {
                const resp = await api.userDocuments.getResourceUri(userId, doc.id);
                const url = resp.data?.url;
                if (!url) return null;
                return {
                    url,
                    fileName: doc.resource_name || cfg.nameEn,
                    titleKey: cfg.labelKey,
                };
            } catch {
                return null;
            } finally {
                setViewing((prev) => ({ ...prev, [cfg.key]: false }));
            }
        },
        [docs, userId]
    );

    const providedCount = useMemo(
        () =>
            INITIAL_DOCS.reduce(
                (count, cfg) => (hasResource(docs[cfg.key]) ? count + 1 : count),
                0
            ),
        [docs]
    );

    return {
        docs,
        loading,
        error,
        uploading,
        viewing,
        providedCount,
        fetchAll,
        upload,
        getPreview,
    };
};

export default useInitialDocuments;
